import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { companies, type CompanyInput } from '@/api/endpoints';
import type { Company } from '@/types';
import { Icon } from '@/components/Icon';
import { Pagination, usePaginated } from '@/components/Pagination';
import { validateUploadSize } from '@/lib/uploadLimits';
import { UploadProgressBar } from '@/components/UploadProgress';

const EMPTY: CompanyInput = {
  fullName: '', shortName: '', issuePlace: '',
  addressLine1: '', addressPostalCode: '', addressCity: '',
  phone: '', email: '', website: '', nip: '', regon: '', bankAccount: '',
  closingText: 'Z wyrazami szacunku', isDefault: false, active: true,
};

export function CompanyManagement() {
  const [rows, setRows] = useState<Company[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<Company | 'new' | null>(null);

  const reload = () => {
    setRows(null); setErr(null);
    companies.adminList().then(setRows).catch(e => setErr(e?.message ?? String(e)));
  };
  useEffect(reload, []);

  const { pagedItems, page, pageSize, setPage, setPageSize, total } = usePaginated(rows ?? [], { storageKey: 'companies' });

  return (
    <div className="max-w-[1400px]">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Firmy wystawiające oferty / umowy</h1>
          <div className="text-ink-400 text-[13px]">Definiuj nagłówki, logo i dane firm, z których wysyłasz dokumenty</div>
        </div>
        <button onClick={() => setEditing('new')} className="btn btn-primary btn-sm">
          <Icon name="plus" className="w-3.5 h-3.5"/> Nowa firma
        </button>
      </header>

      <div className="card">
        <div className="card-body !p-0">
          {err && <div className="text-red-500 p-4">Błąd: {err}</div>}
          {!rows && !err && <div className="text-ink-400 p-4">Ładowanie…</div>}
          {rows && rows.length === 0 && (
            <div className="text-ink-400 p-6 text-center">Brak zdefiniowanych firm.</div>
          )}
          {rows && rows.length > 0 && (
            <>
            <table className="table-hw">
              <thead>
                <tr>
                  <th className="w-16">Logo</th>
                  <th>Nazwa</th>
                  <th>Adres</th>
                  <th>NIP</th>
                  <th>Kontakt</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map(c => (
                  <tr key={c.id} className="hover:bg-ink-50 cursor-pointer" onClick={() => setEditing(c)}>
                    <td>
                      {c.logoUrl
                        ? <img src={c.logoUrl} alt="" className="h-10 max-w-[64px] object-contain"/>
                        : <div className="w-12 h-10 grid place-items-center text-ink-300 text-[10px]">brak</div>}
                    </td>
                    <td>
                      <div className="font-semibold text-ink-900">{c.shortName}</div>
                      <div className="text-[11px] text-ink-500 truncate max-w-[400px]">{c.fullName}</div>
                    </td>
                    <td className="text-[12px] text-ink-700">
                      {c.addressLine1 && <div>{c.addressLine1}</div>}
                      <div className="text-ink-500">{[c.addressPostalCode, c.addressCity].filter(Boolean).join(' ')}</div>
                      <div className="text-ink-400 text-[11px]">Wystawia z: {c.issuePlace}</div>
                    </td>
                    <td className="font-mono text-[12px] text-ink-600">{c.nip ?? '—'}</td>
                    <td className="text-[12px]">
                      {c.email && <div>{c.email}</div>}
                      {c.phone && <div className="text-ink-500 font-mono">{c.phone}</div>}
                    </td>
                    <td>
                      <div className="flex flex-col gap-0.5">
                        {c.isDefault && <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-water-100 text-water-700">Domyślna</span>}
                        <span className={'inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ' + (c.active ? 'bg-green-100 text-green-700' : 'bg-ink-100 text-ink-500')}>
                          {c.active ? 'Aktywna' : 'Nieaktywna'}
                        </span>
                      </div>
                    </td>
                    <td><Icon name="chevron" className="text-ink-300"/></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-3 border-t border-ink-100">
              <Pagination total={total} page={page} pageSize={pageSize}
                          onPageChange={setPage} onPageSizeChange={setPageSize}/>
            </div>
            </>
          )}
        </div>
      </div>

      <CompanyEditor
        open={editing !== null}
        company={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); reload(); }}
      />
    </div>
  );
}

function CompanyEditor({ open, company, onClose, onSaved }: {
  open: boolean;
  company: Company | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [data, setData] = useState<CompanyInput>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Podglądy logo/podpisu trzymamy lokalnie i aktualizujemy z odpowiedzi uploadu —
  // prop `company` jest odświeżany dopiero po przeładowaniu listy, więc na nim
  // pierwszy upload (gdy wcześniej nie było pliku) nigdy nie pokazałby się od razu.
  const [logoUrl, setLogoUrl] = useState<string | undefined>(undefined);
  const [signatureUrl, setSignatureUrl] = useState<string | undefined>(undefined);
  const [uploading, setUploading] = useState<null | 'logo' | 'signature'>(null);
  const [uploadPct, setUploadPct] = useState<number | null>(null); // null = brak wysyłki
  const [uploadDirty, setUploadDirty] = useState(false);
  const logoRef = useRef<HTMLInputElement>(null);
  const signRef = useRef<HTMLInputElement>(null);
  const objUrlRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    resetDirty();
    if (!open) return;
    setErr(null); setBusy(false); setUploading(null); setUploadPct(null); setUploadDirty(false);
    if (objUrlRef.current) { URL.revokeObjectURL(objUrlRef.current); objUrlRef.current = null; }
    setLogoUrl(company?.logoUrl);
    setSignatureUrl(company?.signatureUrl);
    if (company) {
      setData({
        code: company.code,
        fullName: company.fullName,
        shortName: company.shortName,
        addressLine1: company.addressLine1 ?? '',
        addressLine2: company.addressLine2 ?? '',
        addressPostalCode: company.addressPostalCode ?? '',
        addressCity: company.addressCity ?? '',
        issuePlace: company.issuePlace,
        phone: company.phone ?? '',
        email: company.email ?? '',
        website: company.website ?? '',
        nip: company.nip ?? '',
        regon: company.regon ?? '',
        bankAccount: company.bankAccount ?? '',
        closingText: company.closingText,
        isDefault: company.isDefault,
        active: company.active,
      });
    } else {
      setData(EMPTY);
    }
  }, [open, company]);

  const { requestClose, confirmation, dialogProps, resetDirty } = useUnsavedChanges({
    value: data, onClose: () => handleClose(), open, busy: busy || uploading !== null,
  });

  if (!open) return null;

  const set = (k: keyof CompanyInput) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setData({ ...data, [k]: e.target.value });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!data.fullName.trim() || !data.shortName.trim() || !data.issuePlace.trim()) {
      setErr('Pełna nazwa, skrócona nazwa i miejsce wystawienia są wymagane');
      return;
    }
    setBusy(true); setErr(null);
    const op = company
      ? companies.adminUpdate(company.id, data)
      : companies.adminCreate(data);
    op.then(() => onSaved())
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setBusy(false));
  };

  const upload = (which: 'logo' | 'signature', file: File | undefined) => {
    if (!file || !company || uploading) return;
    // Limit rozmiaru pliku (100 MB) — zablokuj przed wysyłką.
    const sizeErr = validateUploadSize(file);
    if (sizeErr) { setErr(sizeErr); return; }
    // Natychmiastowy podgląd z lokalnego pliku, zanim wróci odpowiedź serwera.
    if (objUrlRef.current) URL.revokeObjectURL(objUrlRef.current);
    const localPreview = URL.createObjectURL(file);
    objUrlRef.current = localPreview;
    setUploading(which); setUploadPct(0); setErr(null);
    if (which === 'logo') setLogoUrl(localPreview); else setSignatureUrl(localPreview);

    const op = which === 'logo'
      ? companies.uploadLogo(company.id, file, p => setUploadPct(p.percent))
      : companies.uploadSignature(company.id, file, p => setUploadPct(p.percent));
    // URL jest stały (/api/companies/{id}/logo), więc dobijamy cache-buster,
    // aby przeglądarka pobrała nowy plik zamiast zbuforowanego.
    const bust = (u?: string) => (u ? u + '?v=' + Date.now() : undefined);
    op.then(updated => {
      setUploadDirty(true);
      if (which === 'logo') setLogoUrl(bust(updated.logoUrl));
      else setSignatureUrl(bust(updated.signatureUrl));
    })
      .catch(e => {
        setErr(e?.message ?? String(e));
        // Przy błędzie wróć do poprzedniego podglądu.
        if (which === 'logo') setLogoUrl(company.logoUrl); else setSignatureUrl(company.signatureUrl);
      })
      .finally(() => {
        if (objUrlRef.current) { URL.revokeObjectURL(objUrlRef.current); objUrlRef.current = null; }
        setUploading(null); setUploadPct(null);
        // Wyczyść input, aby ponowny wybór tego samego pliku znów odpalił onChange.
        if (logoRef.current) logoRef.current.value = '';
        if (signRef.current) signRef.current.value = '';
      });
  };

  const handleClose = () => {
    if (uploading) return; // nie zamykaj w trakcie wgrywania
    if (objUrlRef.current) { URL.revokeObjectURL(objUrlRef.current); objUrlRef.current = null; }
    // Upload zapisuje plik na serwerze od razu — jeśli coś wgrano, odśwież listę przy zamknięciu.
    if (uploadDirty) onSaved(); else onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg shadow-xl w-full max-w-[820px] max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-ink-100">
          <h2 className="text-[16px] font-semibold text-ink-900">
            {company ? `Edycja: ${company.shortName}` : 'Nowa firma'}
          </h2>
          <button type="button" onClick={requestClose} className="btn btn-ghost btn-icon text-ink-400 hover:text-ink-700">
            <Icon name="x"/>
          </button>
        </div>

        <form onSubmit={submit} className="p-5 grid grid-cols-2 gap-3">
          <label className="field col-span-2">
            <span className="field-label">Pełna nazwa (drukowana w nagłówku) *</span>
            <input value={data.fullName} onChange={set('fullName')} className="input" placeholder="np. Hydro-Wolski sp. z o.o. — Biuro usług geologicznych…"/>
          </label>
          <label className="field">
            <span className="field-label">Skrócona nazwa *</span>
            <input value={data.shortName} onChange={set('shortName')} className="input" placeholder="Hydro-Wolski"/>
          </label>
          <label className="field">
            <span className="field-label">Miejsce wystawienia *</span>
            <input value={data.issuePlace} onChange={set('issuePlace')} className="input" placeholder="Bobrowiczki"/>
          </label>
          <label className="field col-span-2">
            <span className="field-label">Ulica i numer</span>
            <input value={data.addressLine1 ?? ''} onChange={set('addressLine1')} className="input" placeholder="np. Sławno-Bobrowiczki 40"/>
          </label>
          <label className="field">
            <span className="field-label">Kod pocztowy</span>
            <input value={data.addressPostalCode ?? ''} onChange={set('addressPostalCode')} className="input font-mono" placeholder="76-100"/>
          </label>
          <label className="field">
            <span className="field-label">Miejscowość</span>
            <input value={data.addressCity ?? ''} onChange={set('addressCity')} className="input" placeholder="Sławno"/>
          </label>
          <label className="field">
            <span className="field-label">Telefon</span>
            <input value={data.phone ?? ''} onChange={set('phone')} className="input" placeholder="662-644-734"/>
          </label>
          <label className="field">
            <span className="field-label">E-mail</span>
            <input type="email" value={data.email ?? ''} onChange={set('email')} className="input" placeholder="biuro@…"/>
          </label>
          <label className="field">
            <span className="field-label">Strona WWW</span>
            <input value={data.website ?? ''} onChange={set('website')} className="input" placeholder="https://…"/>
          </label>
          <label className="field">
            <span className="field-label">NIP</span>
            <input value={data.nip ?? ''} onChange={set('nip')} className="input font-mono" placeholder="499-06-77-145"/>
          </label>
          <label className="field">
            <span className="field-label">REGON</span>
            <input value={data.regon ?? ''} onChange={set('regon')} className="input font-mono"/>
          </label>
          <label className="field">
            <span className="field-label">Konto bankowe</span>
            <input value={data.bankAccount ?? ''} onChange={set('bankAccount')} className="input font-mono"/>
          </label>
          <label className="field col-span-2">
            <span className="field-label">Formuła zakończenia (przed podpisem)</span>
            <input value={data.closingText ?? ''} onChange={set('closingText')} className="input" placeholder="Z wyrazami szacunku"/>
          </label>
          <label className="field flex-row items-center gap-2 col-span-1">
            <input type="checkbox" checked={!!data.isDefault} onChange={e => setData({ ...data, isDefault: e.target.checked })}/>
            <span>Domyślna firma w kreatorach</span>
          </label>
          <label className="field flex-row items-center gap-2 col-span-1">
            <input type="checkbox" checked={!!data.active} onChange={e => setData({ ...data, active: e.target.checked })}/>
            <span>Aktywna (dostępna do wyboru)</span>
          </label>

          {company && (
            <div className="col-span-2 grid grid-cols-2 gap-3 border-t border-ink-100 pt-4 mt-2">
              <div>
                <div className="field-label mb-1">Logo (PNG lub JPEG)</div>
                <div className="flex items-center gap-3">
                  <div className="relative w-32 h-16 shrink-0">
                    {logoUrl
                      ? <img src={logoUrl} alt="logo" className="w-full h-full object-contain border border-ink-100 rounded p-1"/>
                      : <div className="w-full h-full grid place-items-center text-ink-400 text-[11px] border border-dashed border-ink-200 rounded">brak logo</div>}
                    {uploading === 'logo' && (
                      <div className="absolute inset-0 grid place-items-center bg-white/70 rounded text-[11px] text-ink-600 gap-1">
                        <span className="inline-block w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin"/>
                        Wgrywanie…
                      </div>
                    )}
                  </div>
                  <input ref={logoRef} type="file" accept="image/png,image/jpeg" onChange={e => upload('logo', e.target.files?.[0])} className="hidden"/>
                  <button type="button" disabled={!!uploading} onClick={() => logoRef.current?.click()} className="btn btn-secondary btn-sm disabled:opacity-50">
                    {uploading === 'logo' ? 'Wgrywanie…' : (logoUrl ? 'Zmień logo…' : 'Wgraj logo…')}
                  </button>
                </div>
                {uploading === 'logo' && uploadPct !== null && (
                  <UploadProgressBar percent={uploadPct} label="Wysyłanie pliku…" compact className="mt-2"/>
                )}
              </div>
              <div>
                <div className="field-label mb-1">Pieczątka z podpisem (PNG lub JPEG)</div>
                <div className="flex items-center gap-3">
                  <div className="relative w-32 h-16 shrink-0">
                    {signatureUrl
                      ? <img src={signatureUrl} alt="pieczątka z podpisem" className="w-full h-full object-contain border border-ink-100 rounded p-1"/>
                      : <div className="w-full h-full grid place-items-center text-ink-400 text-[11px] border border-dashed border-ink-200 rounded">brak</div>}
                    {uploading === 'signature' && (
                      <div className="absolute inset-0 grid place-items-center bg-white/70 rounded text-[11px] text-ink-600 gap-1">
                        <span className="inline-block w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin"/>
                        Wgrywanie…
                      </div>
                    )}
                  </div>
                  <input ref={signRef} type="file" accept="image/png,image/jpeg" onChange={e => upload('signature', e.target.files?.[0])} className="hidden"/>
                  <button type="button" disabled={!!uploading} onClick={() => signRef.current?.click()} className="btn btn-secondary btn-sm disabled:opacity-50">
                    {uploading === 'signature' ? 'Wgrywanie…' : (signatureUrl ? 'Zmień pieczątkę…' : 'Wgraj pieczątkę…')}
                  </button>
                </div>
                {uploading === 'signature' && uploadPct !== null && (
                  <UploadProgressBar percent={uploadPct} label="Wysyłanie pliku…" compact className="mt-2"/>
                )}
              </div>
            </div>
          )}

          {!company && (
            <div className="col-span-2 text-[11px] text-ink-500 bg-ink-50 p-2 rounded">
              Logo i pieczątkę z podpisem będzie można wgrać po zapisaniu firmy.
            </div>
          )}

          {err && <div className="col-span-2 text-red-500 text-[12px]">Błąd: {err}</div>}

          <div className="col-span-2 flex justify-end gap-2 pt-2 border-t border-ink-100">
            <button type="button" onClick={requestClose} className="btn btn-secondary">Anuluj</button>
            <button type="submit" disabled={busy || !!uploading} className="btn btn-primary">
              {busy ? 'Zapisywanie…' : (company ? 'Zapisz zmiany' : 'Utwórz firmę')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
